import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Field, ListInput } from "@/components/common";

const TEXT = [["business_objective", "Business objective", 2], ["target_audience", "Target audience", 2], ["offer", "Offer"], ["market", "Market"],
  ["duration", "Duration"], ["budget", "Budget"], ["primary_conversion_action", "Primary conversion action", 2]];
const LISTS = [["proposed_channels", "Proposed channels"], ["success_metrics", "Success metrics"], ["constraints", "Constraints"], ["assumptions", "Assumptions"]];

export default function InterpretationEditor({ command, interpretation, onDiscard }) {
  const { path, refresh } = useWorkspace();
  const nav = useNavigate();
  const [data, setData] = useState(interpretation);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const proceed = async () => {
    setBusy(true);
    try {
      const { data: c } = await api.post(path("/campaigns"), { command, interpretation: data });
      await api.post(path(`/campaigns/${c.id}/generate-strategy`));
      refresh();
      nav(`/campaigns/${c.id}`);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <section data-testid="interpretation-editor" className="cq-card p-5 sm:p-8 cq-rise">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="cq-label mb-2">Step 2 · Confirm interpretation</div>
          <h2 className="font-display text-2xl font-semibold text-[#F7FAFF]">Here's how CRESQEN read your command</h2>
          <p className="mt-2 text-sm text-[#AAB6CC] font-mono">› {command}</p>
        </div>
        <button data-testid="interpretation-discard-button" onClick={onDiscard} className="cq-btn cq-btn-ghost !p-2" title="Discard"><X size={16} /></button>
      </div>
      <p className="mt-4 text-xs text-[#7C8BAD]">Every field is editable. Nothing is generated or launched until you continue.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {TEXT.map(([k, label, span]) => (
          <div key={k} className={span === 2 ? "sm:col-span-2" : ""}>
            <Field label={label}><textarea data-testid={`interp-${k}`} rows={span === 2 ? 2 : 1} className="cq-input resize-y" value={data[k] || ""} onChange={(e) => set(k)(e.target.value)} /></Field>
          </div>
        ))}
        {LISTS.map(([k, label]) => (
          <Field key={k} label={label}><ListInput testId={`interp-${k}`} rows={4} value={data[k]} onChange={set(k)} /></Field>
        ))}
      </div>
      <div className="mt-8 flex flex-col sm:flex-row justify-end gap-2">
        <button data-testid="interpretation-cancel-button" onClick={onDiscard} className="cq-btn cq-btn-ghost">Cancel</button>
        <button data-testid="interpretation-approve-button" disabled={busy} onClick={proceed} className="cq-btn cq-btn-primary">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} Approve & draft strategy <ArrowRight size={14} />
        </button>
      </div>
    </section>
  );
}
